import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/app");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-shell">
      <div className="relative h-48 overflow-hidden" style={{ background: "radial-gradient(500px 200px at 50% -20%, rgba(60,255,126,0.25), transparent 55%), linear-gradient(180deg, #0B1E5B 0%, #07122F 100%)" }}>
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
        <Link to="/" className="absolute top-5 left-5 text-white z-10" data-testid="login-back-btn">
          <ArrowLeft />
        </Link>
        <div className="absolute inset-0 flex items-center justify-center flex-col gap-2">
          <div className="w-20 h-20 rounded-full overflow-hidden ring-2 ring-[#3CFF7E]/40">
            <img src={LOGO_URL} alt="AutoFix Rescue" className="w-full h-full object-cover scale-110" />
          </div>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#3CFF7E] font-display">
            Sign In
          </div>
        </div>
      </div>

      <div className="px-6 pt-6">
        <h1 className="font-display text-4xl font-black leading-none text-white">Welcome back.</h1>
      </div>

      <form onSubmit={submit} className="px-6 py-8 space-y-4 anim-slide-up">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
            Email
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
            placeholder="you@example.com"
            className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 bg-transparent focus:outline-none focus:border-[#3CFF7E] focus:ring-2 focus:ring-[#3CFF7E]/20"
          />
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
            Password
          </label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input"
            placeholder="••••••••"
            className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 bg-transparent focus:outline-none focus:border-[#3CFF7E] focus:ring-2 focus:ring-[#3CFF7E]/20"
          />
        </div>

        {error && (
          <div className="text-sm text-[#F87171] border-l-2 border-[#F87171] pl-3" data-testid="login-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit-btn"
          className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md active:scale-[0.98] transition-transform disabled:opacity-60 shadow-[0_0_40px_rgba(60,255,126,0.35)]"
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>

        <div className="text-center -mt-1">
          <Link to="/forgot" data-testid="login-forgot-link" className="text-sm text-[#9CA3AF] hover:text-[#3CFF7E] underline underline-offset-4">
            Forgot password?
          </Link>
        </div>

        <p className="text-sm text-[#9CA3AF] text-center">
          No account?{" "}
          <Link to="/register" className="font-bold text-[#3CFF7E]" data-testid="login-register-link">
            Create one
          </Link>
        </p>
      </form>
    </div>
  );
}
